/**
 * Superadmin — Subscription Plans Page
 * CRUD for subscription plans
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, PencilSquareIcon, TrashIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/api/v1';
function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
}

const EMPTY_PLAN = {
  name: '', slug: '', description: '',
  priceMonthly: 0, priceAnnual: 0, currency: 'EUR',
  maxStaff: 5, maxLocations: 1, maxBookingsPerMonth: 100,
  features: [], isVisible: true, isFeatured: false, sortOrder: 999, badgeText: ''
};

const ALL_FEATURES = [
  { key: 'online_booking', label: 'Online Booking' },
  { key: 'email_reminders', label: 'Email Reminders' },
  { key: 'sms_notifications', label: 'SMS Notifications' },
  { key: 'reports', label: 'Reports' },
  { key: 'api_access', label: 'API Access' },
  { key: 'white_label', label: 'White Label' }
];

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [editPlan, setEditPlan] = useState(null); // null = closed, {} = new, { id, ...} = editing
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/plans`, { headers: getAuthHeaders() });
      setPlans(res.data.plans);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load plans' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        ...editPlan,
        priceMonthly: Number(editPlan.priceMonthly),
        priceAnnual: Number(editPlan.priceAnnual),
        maxStaff: Number(editPlan.maxStaff),
        maxLocations: Number(editPlan.maxLocations),
        maxBookingsPerMonth: Number(editPlan.maxBookingsPerMonth),
        sortOrder: Number(editPlan.sortOrder),
        badgeText: editPlan.badgeText || null
      };

      if (editPlan.id) {
        await axios.put(`${API}/superadmin/plans/${editPlan.id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/superadmin/plans`, payload, { headers: getAuthHeaders() });
      }

      setMessage({ type: 'success', text: `Plan ${editPlan.id ? 'updated' : 'created'} successfully` });
      setEditPlan(null);
      fetchPlans();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save plan' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/superadmin/plans/${plan.id}`, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: 'Plan deleted' });
      fetchPlans();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete plan' });
    }
  };

  const toggleFeature = (key) => {
    const features = editPlan.features || [];
    setEditPlan(p => ({
      ...p,
      features: features.includes(key) ? features.filter(f => f !== key) : [...features, key]
    }));
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
        <button
          onClick={() => setEditPlan({ ...EMPTY_PLAN })}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          New Plan
        </button>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationCircleIcon className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 text-center py-12 text-gray-400">Loading...</div>
        ) : plans.map(plan => (
          <div key={plan.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                  {plan.isFeatured && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Featured</span>}
                  {!plan.isVisible && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Hidden</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{plan.slug}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-gray-900">€{plan.priceMonthly}<span className="text-xs font-normal text-gray-500">/mo</span></p>
                <p className="text-xs text-gray-400">€{plan.priceAnnual}/yr</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3">{plan.description}</p>
            <div className="text-xs text-gray-500 space-y-1 mb-4">
              <p>👥 Up to {plan.maxStaff} staff · 📍 {plan.maxLocations} location(s)</p>
              <p>📅 {plan.maxBookingsPerMonth} bookings/month</p>
            </div>
            <div className="flex flex-wrap gap-1 mb-4">
              {(plan.features || []).map(f => (
                <span key={f} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{f}</span>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setEditPlan({ ...plan, badgeText: plan.badgeText || '' })}
                className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 border border-purple-200 hover:bg-purple-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleDelete(plan)}
                className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editPlan && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">
              {editPlan.id ? `Edit Plan: ${editPlan.name}` : 'New Plan'}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input value={editPlan.name} onChange={e => setEditPlan(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input value={editPlan.slug} onChange={e => setEditPlan(p => ({ ...p, slug: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="lowercase-with-hyphens" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input value={editPlan.description || ''} onChange={e => setEditPlan(p => ({ ...p, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price (€)</label>
                <input type="number" step="0.01" value={editPlan.priceMonthly}
                  onChange={e => setEditPlan(p => ({ ...p, priceMonthly: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual Price (€)</label>
                <input type="number" step="0.01" value={editPlan.priceAnnual}
                  onChange={e => setEditPlan(p => ({ ...p, priceAnnual: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Staff</label>
                <input type="number" value={editPlan.maxStaff}
                  onChange={e => setEditPlan(p => ({ ...p, maxStaff: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Locations</label>
                <input type="number" value={editPlan.maxLocations}
                  onChange={e => setEditPlan(p => ({ ...p, maxLocations: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Bookings/Month</label>
                <input type="number" value={editPlan.maxBookingsPerMonth}
                  onChange={e => setEditPlan(p => ({ ...p, maxBookingsPerMonth: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={editPlan.sortOrder}
                  onChange={e => setEditPlan(p => ({ ...p, sortOrder: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_FEATURES.map(f => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggleFeature(f.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        (editPlan.features || []).includes(f.key)
                          ? 'bg-purple-600 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2 flex gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!editPlan.isVisible} onChange={e => setEditPlan(p => ({ ...p, isVisible: e.target.checked }))} className="rounded" />
                  Visible to public
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!editPlan.isFeatured} onChange={e => setEditPlan(p => ({ ...p, isFeatured: e.target.checked }))} className="rounded" />
                  Featured
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditPlan(null)} className="flex-1 border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !editPlan.name || !editPlan.slug}
                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                {saving ? 'Saving...' : 'Save Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
